import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

// Connects to data-controller="celebrate"
// Fires a single, tasteful confetti burst + scales in the success check when the
// order-confirmation modal opens. One-shot per order (a sessionStorage flag keyed
// by order id) so navigating back doesn't re-fire it. Fully gated behind
// prefers-reduced-motion: reduce → static, no confetti, instant.
export default class extends Controller {
  static values = { orderId: String }
  static targets = ["check"]

  connect() {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce) return

    // Only celebrate the first time this order's confirmation is shown.
    const key = `celebrated:${this.orderIdValue}`
    try {
      if (sessionStorage.getItem(key)) return
      sessionStorage.setItem(key, "1")
    } catch (_) { /* private mode — celebrate anyway */ }

    this.scaleInCheck()
    // Let the modal finish opening before the burst so it's anchored, not janky.
    this.timer = setTimeout(() => this.burst(), 180)
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    if (this.canvas) this.canvas.remove()
  }

  // canvas-confetti's default canvas sits at z-index 100 — behind the Bootstrap
  // modal (z 1050+), so nothing shows over the confirmation. Mount our own
  // full-viewport canvas above the modal and fire into it.
  confettiFn() {
    if (this._confetti) return this._confetti
    const canvas = document.createElement("canvas")
    canvas.style.cssText =
      "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2000;"
    document.body.appendChild(canvas)
    this.canvas = canvas
    this._confetti = confetti.create(canvas, { resize: true, useWorker: true })
    return this._confetti
  }

  scaleInCheck() {
    if (this.hasCheckTarget) this.checkTarget.classList.add("celebrate-pop")
  }

  // Prominent celebration: a center burst over the header plus two side cannons,
  // with a long enough lifetime (ticks) that the pieces rain all the way down
  // the page. The old single short burst hugged the top and got lost on mobile.
  burst() {
    const fire = this.confettiFn()
    const rect = this.element.getBoundingClientRect()
    const common = {
      startVelocity: 50,
      gravity: 0.9,
      ticks: 320,           // ~2s — long enough to fall the full viewport
      scalar: 1,
      disableForReducedMotion: true,
    }

    // Center burst anchored above the success check.
    fire({
      ...common,
      particleCount: 90,
      spread: 75,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: Math.max(0.08, (rect.top + 40) / window.innerHeight),
      },
    })
    // Side cannons angled inward — they arc across and rain down the whole page.
    fire({ ...common, particleCount: 45, angle: 60, spread: 55, origin: { x: 0, y: 0.25 } })
    fire({ ...common, particleCount: 45, angle: 120, spread: 55, origin: { x: 1, y: 0.25 } })
  }
}
